import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import AppShell from './layouts/AppShell';
import DashboardPage from './pages/DashboardPage';
import TenantsPage from './pages/TenantsPage';
import TenantDetailPage from './pages/TenantDetailPage';
import InvoicesPage from './pages/InvoicesPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import ArchivePage from './pages/ArchivePage';
import ValidationRulesPage from './pages/ValidationRulesPage';
import AuditLogPage from './pages/AuditLogPage';
import MonitoringPage from './pages/MonitoringPage';
import TickstarPage from './pages/TickstarPage';
import CertificatesPage from './pages/CertificatesPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import AboutPage from './pages/AboutPage';
import ToolsPage from './pages/ToolsPage';
import KycReviewQueuePage from './pages/KycReviewQueuePage';
import TaxReviewQueuePage from './pages/TaxReviewQueuePage';
import ComplianceDashboardPage from './pages/ComplianceDashboardPage';
import InsurancesPage from './pages/InsurancesPage';
import IsoCertificationsPage from './pages/IsoCertificationsPage';
import LeadsPage from './pages/LeadsPage';
import LeadCreatePage from './pages/LeadCreatePage';
import LeadDetailPage from './pages/LeadDetailPage';
import OpportunitiesKanbanPage from './pages/OpportunitiesKanbanPage';
import OpportunityDetailPage from './pages/OpportunityDetailPage';
import CrmReportsPage from './pages/CrmReportsPage';
import AdminTasksPage from './pages/AdminTasksPage';
import GlAccountsPage from './pages/GlAccountsPage';
import AdminChatPage from './pages/AdminChatPage';
import ErpConnectorsPage from './pages/ErpConnectorsPage';
import NewsletterPage from './pages/NewsletterPage';
import WorkflowDefinitionsPage from './pages/WorkflowDefinitionsPage';
import IdpConnectionsPage from './pages/IdpConnectionsPage';
import SecurityIncidentsPage from './pages/SecurityIncidentsPage';
import CustomRolesPage from './pages/CustomRolesPage';
import LoginPage from './pages/LoginPage';
import { useSession } from './auth/SessionContext';

function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useSession();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireAuth><AppShell /></RequireAuth>}>
        <Route index element={<DashboardPage />} />
        <Route path="/tenants" element={<TenantsPage />} />
        <Route path="/tenants/:tenantId" element={<TenantDetailPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/:invoiceId" element={<InvoiceDetailPage />} />
        <Route path="/archive" element={<ArchivePage />} />
        <Route path="/rules" element={<ValidationRulesPage />} />
        <Route path="/audit" element={<AuditLogPage />} />
        <Route path="/monitoring" element={<MonitoringPage />} />
        <Route path="/tickstar" element={<TickstarPage />} />
        <Route path="/certificates" element={<CertificatesPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/tools" element={<ToolsPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        {/* P0 routes added 2026-05-21 */}
        <Route path="/kyc-review" element={<KycReviewQueuePage />} />
        <Route path="/tax-review" element={<TaxReviewQueuePage />} />
        <Route path="/compliance/dashboard" element={<ComplianceDashboardPage />} />
        <Route path="/compliance/insurances" element={<InsurancesPage />} />
        <Route path="/compliance/iso" element={<IsoCertificationsPage />} />
        {/* M1 CRM admin routes (P1 — backend ready, UI skeleton) */}
        <Route path="/crm/leads" element={<LeadsPage />} />
        <Route path="/crm/leads/new" element={<LeadCreatePage />} />
        <Route path="/crm/leads/:leadId" element={<LeadDetailPage />} />
        <Route path="/crm/opportunities" element={<OpportunitiesKanbanPage />} />
        <Route path="/crm/opportunities/:opportunityId" element={<OpportunityDetailPage />} />
        <Route path="/crm/reports" element={<CrmReportsPage />} />
        <Route path="/tasks" element={<AdminTasksPage />} />
        <Route path="/accounting/gl-accounts" element={<GlAccountsPage />} />
        <Route path="/chat" element={<AdminChatPage />} />
        <Route path="/integrations/erp" element={<ErpConnectorsPage />} />
        <Route path="/notifications/newsletter" element={<NewsletterPage />} />
        <Route path="/workflow/definitions" element={<WorkflowDefinitionsPage />} />
        <Route path="/security/idp" element={<IdpConnectionsPage />} />
        <Route path="/security/incidents" element={<SecurityIncidentsPage />} />
        <Route path="/security/roles" element={<CustomRolesPage />} />
      </Route>
    </Routes>
  );
}
